import { useEffect, useMemo, useRef, type ComponentProps } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import type { ChatMessageType } from '@/store/types';
import MarkdownWithMath from './MarkdownWithMath';
import { useSmoothTypewriter } from './useSmoothTypewriter';
import {
  decodeSuggestedQuestionLink,
  isSuggestedQuestionLink,
  SUGGESTED_QUESTION_LINK_PREFIX,
  transformSuggestedQuestionTags,
} from './parseSuggestedQuestions';

type BotMessageTextProps = {
  msg: ChatMessageType;
  isLiveStream: boolean;
  streamBufferClosed: boolean;
  suggestionsDisabled?: boolean;
  normalizeMarkdown: (text: string) => string;
  markdownComponents: ComponentProps<typeof ReactMarkdown>['components'];
  onRevealCaughtUp: () => void;
  onSyncInterruptedDisplay: (messageId: string, displayText: string) => void;
  onSuggestedQuestionClick?: (question: string) => void;
  onVisibleTick?: () => void;
};

export default function BotMessageText({
  msg,
  isLiveStream,
  streamBufferClosed,
  suggestionsDisabled = false,
  normalizeMarkdown,
  markdownComponents,
  onRevealCaughtUp,
  onSyncInterruptedDisplay,
  onSuggestedQuestionClick,
  onVisibleTick,
}: BotMessageTextProps) {
  const target = isLiveStream ? (msg.streamTarget ?? '') : msg.message;
  const streamBufferClosedRef = useRef(streamBufferClosed);
  streamBufferClosedRef.current = streamBufferClosed;

  const { visible, visibleRef } = useSmoothTypewriter(target, {
    active: isLiveStream,
    streamId: msg.id,
    onCaughtUp: () => {
      if (streamBufferClosedRef.current) onRevealCaughtUp();
    },
  });

  const displayText = isLiveStream || (msg.interrupted && !msg.message.trim()) ? visible : msg.message;

  const markdownWithSuggestedQuestions = useMemo(
    () => normalizeMarkdown(transformSuggestedQuestionTags(displayText)),
    [displayText, normalizeMarkdown],
  );

  const hasSuggestedQuestions = markdownWithSuggestedQuestions.includes(SUGGESTED_QUESTION_LINK_PREFIX);

  const resolvedMarkdownComponents = useMemo<ComponentProps<typeof ReactMarkdown>['components']>(() => {
    const defaultAnchor = markdownComponents?.a;

    return {
      ...markdownComponents,
      a: ({ href, children, ...props }) => {
        if (isSuggestedQuestionLink(href)) {
          const question = decodeSuggestedQuestionLink(href);

          return (
            <button
              type="button"
              className="aida-suggested-questions__chip"
              disabled={suggestionsDisabled || !onSuggestedQuestionClick}
              onClick={event => {
                event.preventDefault();
                onSuggestedQuestionClick?.(question);
              }}
            >
              {children}
            </button>
          );
        }

        if (defaultAnchor) {
          const AnchorComponent = defaultAnchor;
          return (
            <AnchorComponent href={href} {...props}>
              {children}
            </AnchorComponent>
          );
        }

        return (
          <a href={href ?? undefined} target="_blank" rel="noopener noreferrer" {...props}>
            {children}
          </a>
        );
      },
    };
  }, [markdownComponents, onSuggestedQuestionClick, suggestionsDisabled]);

  const didMountFinalizeRef = useRef(false);
  useEffect(() => {
    if (didMountFinalizeRef.current) return;
    didMountFinalizeRef.current = true;
    if (isLiveStream && streamBufferClosed) {
      onRevealCaughtUp();
    }
  }, []);

  useEffect(() => {
    if (!isLiveStream || !streamBufferClosed) return;
    if (visible.length >= target.length) {
      onRevealCaughtUp();
    }
  }, [isLiveStream, streamBufferClosed, visible, target, onRevealCaughtUp]);

  const didSyncInterruptedRef = useRef(false);
  useEffect(() => {
    if (!msg.interrupted) {
      didSyncInterruptedRef.current = false;
      return;
    }
    if (isLiveStream || didSyncInterruptedRef.current) return;
    didSyncInterruptedRef.current = true;
    onSyncInterruptedDisplay(msg.id, visibleRef.current);
  }, [msg.id, msg.interrupted, isLiveStream, onSyncInterruptedDisplay, visibleRef]);

  useEffect(() => {
    if (isLiveStream && visible) onVisibleTick?.();
  }, [isLiveStream, visible, onVisibleTick]);

  if (!markdownWithSuggestedQuestions.trim() && !msg.interrupted) return null;

  return (
    <div
      className={`aida-msg__text${isLiveStream ? ' aida-msg__text--typing' : ''}${
        hasSuggestedQuestions ? ' aida-msg__text--with-inline-questions' : ''
      }`}
    >
      <MarkdownWithMath
        content={markdownWithSuggestedQuestions}
        markdownComponents={resolvedMarkdownComponents}
      />
    </div>
  );
}
